// src/index.js - ENHANCED (Add to existing index.js)
// NON-BREAKING enhancement to existing index.js

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { ErrorTrackingService } from './services/ErrorTrackingService';
import { SanitizationService } from './services/SanitizationService';

const initializeSanitization = () => {
  try {
    console.log('🛡️ Running sanitization health check...');
    const healthCheck = SanitizationService.performHealthCheck();
    console.log('✅ Sanitization service ready:', healthCheck.healthy ? 'PASS' : 'FAIL');
  } catch (error) {
    console.warn('⚠️ Sanitization service check failed:', error);
  }
};


// ENHANCEMENT: Add error tracking initialization

// Initialize error tracking before app starts
const initializeErrorTracking = () => {
  try {
    console.log('🚀 Initializing enhanced error tracking...');
    
    const success = ErrorTrackingService.initialize({
      // Only enable in production or when explicitly requested
      enabled: process.env.NODE_ENV === 'production' || 
               process.env.REACT_APP_ENABLE_ERROR_TRACKING === 'true',
    });

    if (success) {
      console.log('✅ Error tracking initialized');
      
      // Make ErrorTrackingService available for testing (development only)
      if (process.env.NODE_ENV === 'development') {
        window.ErrorTrackingService = ErrorTrackingService;
        console.log('🧪 ErrorTrackingService available on window for testing');
      }
      
      // Add global error handling enhancement (non-breaking)
      const originalWindowErrorHandler = window.onerror;
      window.onerror = function(message, source, lineno, colno, error) {
        // Capture in Sentry without disrupting existing behavior
        ErrorTrackingService.captureError(error || new Error(message), {
          source,
          lineno,
          colno,
          component: 'global',
        });

        // Call original handler if it exists
        if (originalWindowErrorHandler) {
          return originalWindowErrorHandler.apply(this, arguments);
        }
        
        return false;
      };

      // Unhandled promise rejection enhancement
      const originalUnhandledRejection = window.onunhandledrejection;
      window.onunhandledrejection = function(event) {
        ErrorTrackingService.captureError(event.reason, {
          component: 'promise',
          action: 'unhandled_rejection',
        });

        // Call original handler if it exists
        if (originalUnhandledRejection) {
          return originalUnhandledRejection.apply(this, arguments);
        }
      };

    } else {
      console.log('⚠️ Error tracking in fallback mode');
    }

  } catch (error) {
    // CRITICAL: Never let error tracking break the app startup
    console.warn('⚠️ Error tracking initialization failed:', error);
  }
};

// Initialize error tracking
initializeErrorTracking();
initializeSanitization();

// EXISTING CODE REMAINS UNCHANGED
const root = ReactDOM.createRoot(document.getElementById('root'));

// Enhanced Root Component with Error Boundary (non-breaking addition)
const EnhancedApp = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// Simple Error Boundary that enhances existing error handling
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state to show fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Enhance error reporting without breaking existing patterns
    console.error('🚨 Error Boundary caught error:', error, errorInfo);
    
    // Capture in Sentry with enhanced context
    ErrorTrackingService.captureError(error, {
      component: 'ErrorBoundary',
      action: 'componentDidCatch',
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Log additional context for debugging
    console.error('Component Stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Simple fallback UI that doesn't disrupt the app design
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f9fafb',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '400px',
            textAlign: 'center',
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>
              Something went wrong
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              We're sorry for the inconvenience. Please refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Refresh Page
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Render the enhanced app
root.render(
  <React.StrictMode>
    <EnhancedApp />
  </React.StrictMode>
);

// Enhanced service worker registration (if you use service workers)
// This maintains existing functionality while adding error tracking
/* if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
        ErrorTrackingService.addBreadcrumb('Service worker registered', 'info');
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
        ErrorTrackingService.captureError(registrationError, {
          component: 'serviceWorker',
          action: 'registration_failed',
        });
      });
  });
} */